// src/telephony/SoftphoneWidget.tsx
//
// Floating softphone, available on every page.
//
// The dialer used to exist only as a page, so an agent could only take a call
// while sitting on that one screen — navigating away tore down the
// registration. This widget renders from the module-level store in
// ./softphoneStore, which owns the SIP session, so the line stays up wherever
// the agent is in the portal.
//
// Collapsed it is a single button; it expands on click and auto-expands on an
// incoming call, because a ringing phone the agent cannot see is worse than no
// phone at all.
import React, { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { Phone, PhoneOff, PhoneCall, Wifi, WifiOff, X, Maximize2, Forward, Pause, Play, Users, Check } from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import ContactEditModal from 'components/Contacts/ContactEditModal';
import { agentConsoleService, DISPOSITION_CODES, fetchDispositionCodes } from 'api/services/agentConsole.service';
import { subscribe, getState, actions, Phase } from './softphoneStore';

// Every organization role: this is the phone an agent answers and calls
// colleagues from, so gating it to admins would exclude the people it is for.
// Safe because the credential endpoint provisions only for the CALLING
// principal, into that caller's own organization context — a phone can only
// ever reach its own tenant. Carrier-side roles are absent on purpose: they need
// not belong to an organization, so there is no context to place a phone in.
// Keep in sync with SoftphoneController's @PreAuthorize and the sidebar.
const ALLOWED_ROLES = ['master_admin', 'super_admin', 'service_admin', 'service_user', 'operator_admin', 'operator_user'];
const LS_OPEN = 'teeoff.softphone.widget.open';

const PHASE_STYLE: Record<Phase, { dot: string; label: string }> = {
  offline: { dot: 'bg-gray-400', label: 'Offline' },
  connecting: { dot: 'bg-amber-400 animate-pulse', label: 'Connecting…' },
  registered: { dot: 'bg-green-500', label: 'Ready' },
  ringing: { dot: 'bg-purple-500 animate-pulse', label: 'Incoming call' },
  calling: { dot: 'bg-amber-500 animate-pulse', label: 'Calling…' },
  'in-call': { dot: 'bg-blue-500', label: 'On call' },
};

const SoftphoneWidget: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const role = (currentOrganization?.role || user?.role || '').toLowerCase();
  const roleAllows = ALLOWED_ROLES.includes(role);

  // Role alone was the whole gate, which put a phone on the screen of every
  // admin in every organisation — including tenants with no agent seats and no
  // outbound calling, where it could never place or receive anything. A control
  // that cannot work is worse than an absent one: it invites a support ticket
  // and implies a capability that was never bought.
  //
  // So the SERVER decides, from what actually exists: does this person hold a
  // seat, and may this organisation dial out. Undefined while it loads, which
  // renders nothing — the phone appearing a moment late is unnoticeable, and
  // flashing it at someone who may not have one is not.
  const [seat, setSeat] = useState<{ hasSeat: boolean; outboundEnabled: boolean; softphoneAllowed: boolean } | undefined>();

  useEffect(() => {
    if (!roleAllows || !user) { setSeat(undefined); return; }
    let alive = true;
    agentConsoleService.mySeat()
      .then(res => {
        if (!alive) return;
        setSeat({ hasSeat: !!res.seat, outboundEnabled: res.outboundEnabled === true, softphoneAllowed: res.softphoneAllowed });
      })
      // A failed probe leaves it hidden rather than shown. The safe default for
      // a phone that dials real numbers is "not there".
      .catch(() => { if (alive) setSeat({ hasSeat: false, outboundEnabled: false, softphoneAllowed: false }); });
    return () => { alive = false; };
  }, [roleAllows, user, currentOrganization]);

  // …and the organisation has purchased seats (2026-10-14): the server will
  // not issue an unentitled org's credential, so the widget must not offer it.
  const enabled = roleAllows && seat?.hasSeat === true && seat?.outboundEnabled === true
    && seat?.softphoneAllowed === true;

  const state = useSyncExternalStore(subscribe, getState);
  const [open, setOpen] = useState<boolean>(() => localStorage.getItem(LS_OPEN) === '1');
  const [dest, setDest] = useState('1000');
  const [xfer, setXfer] = useState('');
  const [showXfer, setShowXfer] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  // Captured when the save is opened, not read at submit time: the call can end
  // — clearing state.caller — while the agent is still typing the name, and a
  // contact saved with a blank number is worse than no contact at all.
  const [saveNumber, setSaveNumber] = useState('');
  // Wrap-up for the call that just ended. Captured on the transition OUT of a
  // call, because state.caller is cleared the moment the session tears down —
  // the same trap as the save-contact capture.
  const [wrapUp, setWrapUp] = useState<{ number: string } | null>(null);
  // The codes the SERVER accepts, so the buttons cannot drift from what the
  // API validates. Falls back to the built-in list if the request fails —
  // an agent must never be shown an empty wrap-up dialog.
  const [codes, setCodes] = useState(DISPOSITION_CODES);
  // Only once there is a session. This widget is mounted outside the routes,
  // so it also renders on /login, where every request is a guaranteed 401 —
  // and a 401 used to bounce the browser to /login, reloading the page the
  // widget had just mounted on. The defaults already cover the unauthenticated
  // case, so asking gains nothing.
  useEffect(() => {
    if (!user) return;
    fetchDispositionCodes().then(setCodes);
  }, [user]);

  useEffect(() => { localStorage.setItem(LS_OPEN, open ? '1' : '0'); }, [open]);

  // A ringing phone the agent cannot see is useless, so force it visible.
  useEffect(() => { if (state.phase === 'ringing') setOpen(true); }, [state.phase]);

  // Offer a code when a call ENDS. Tracked via a ref rather than by watching
  // `phase` alone: 'registered' is also the state before any call, and prompting
  // an agent to code a call they never had is how a wrap-up gets ignored.
  const wasOnCall = React.useRef(false);
  useEffect(() => {
    const onCall = state.phase === 'in-call';
    if (onCall) wasOnCall.current = true;
    else if (wasOnCall.current && state.phase !== 'ringing' && state.phase !== 'calling') {
      wasOnCall.current = false;
      setWrapUp({ number: state.caller || '' });
      setOpen(true);
    }
  }, [state.phase, state.caller]);

  const hangup = useCallback(() => { actions.hangup(); }, []);

  if (!enabled) return null;

  const { phase } = state;
  const style = PHASE_STYLE[phase];
  const ringing = phase === 'ringing';
  const busy = phase === 'in-call' || phase === 'calling';
  const online = phase !== 'offline' && phase !== 'connecting';

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        title={`Softphone — ${style.label}`}
        aria-label={`Open softphone, ${style.label}`}
        className={`fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center
          text-white transition-transform hover:scale-105 ${
          ringing ? 'bg-purple-600 animate-bounce' : busy ? 'bg-blue-600' : online ? 'bg-green-600' : 'bg-gray-500'}`}
      >
        <PhoneCall className="w-6 h-6" />
        <span className={`absolute top-1 right-1 w-3 h-3 rounded-full ring-2 ring-white ${style.dot}`} />
      </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-50 w-80 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
      <div className="px-3 py-2 flex items-center justify-between bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <span className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
          <span className={`w-2.5 h-2.5 rounded-full ${style.dot}`} />
          {style.label}
        </span>
        <span className="flex items-center gap-1">
          <Link to="/telephony/softphone" title="Open full softphone"
            className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500">
            <Maximize2 className="w-4 h-4" />
          </Link>
          <button onClick={() => setOpen(false)} title="Minimise"
            className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500">
            <X className="w-4 h-4" />
          </button>
        </span>
      </div>

      <div className="p-3 space-y-3">
        {/* Who is calling, plus whatever the pre-answer lookup and the SIP
            headers turned up. Stays visible for the whole call. */}
        {(ringing || busy) && (state.caller || Object.keys(state.inHeaders).length > 0) && (
          <div className={`rounded-lg p-2.5 text-xs ${ringing
            ? 'bg-purple-50 dark:bg-purple-900/30' : 'bg-blue-50 dark:bg-blue-900/30'}`}>
            {state.conferenceRoom
              ? <div>
                  <div className="flex items-center gap-1.5 text-sm font-medium text-indigo-700 dark:text-indigo-300">
                    <Users className="w-4 h-4" /> Conference {state.conferenceRoom}
                    <span className="opacity-70">· {state.participants.length} in room</span>
                  </div>
                  {/* Naming who is present: "1 in room" and "3 in room" sound
                      identical until somebody speaks. */}
                  {state.participants.length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {state.participants.map(p => (
                        <li key={p.channel} className="font-mono text-gray-700 dark:text-gray-300 truncate">
                          {p.callerId || p.channel}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              : state.caller && <div className="font-mono text-sm text-gray-900 dark:text-gray-100 truncate">{state.caller}</div>}
            {state.profileState === 'loading' && <div className="mt-1 text-gray-500">looking up caller…</div>}
            {/* An unknown caller is the ONLY case that offers a save. A colleague
                is already described by the directory, and a lookup that merely
                failed must not invite creating a duplicate of a contact that
                probably exists. */}
            {state.profileState === 'none' && (
              <div className="mt-1 flex items-center gap-2">
                <span className="text-gray-500">not in contacts</span>
                <button
                  type="button"
                  onClick={() => { setSaveNumber(state.caller); setSaveOpen(true); }}
                  className="rounded border border-gray-300 px-1.5 py-0.5 text-[11px] font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  Save contact
                </button>
              </div>
            )}
            {state.profileState === 'error' && <div className="mt-1 text-amber-600 dark:text-amber-400">lookup unavailable</div>}
            {state.profileState === 'found' && state.profile && (
              <div className="mt-1 text-gray-800 dark:text-gray-200">
                {state.profile.name || [state.profile.firstName, state.profile.lastName].filter(Boolean).join(' ')}
                {state.profileIsInternal
                  ? <span className="ml-1 rounded bg-gray-200 px-1 text-[10px] uppercase tracking-wide text-gray-700 dark:bg-gray-700 dark:text-gray-200">colleague</span>
                  : (state.profile.company ? ` · ${state.profile.company}` : '')}
              </div>
            )}
            {Object.entries(state.inHeaders).map(([k, v]) => (
              <div key={k} className="mt-0.5 flex gap-2 text-gray-600 dark:text-gray-300">
                <span className="opacity-70">{k.replace(/^x-/i, '')}</span>
                <span className="font-mono">{v}</span>
              </div>
            ))}
          </div>
        )}

        {ringing ? (
          <div className="flex gap-2">
            <button onClick={() => actions.answer()}
              className="flex-1 px-3 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 inline-flex items-center justify-center gap-2 text-sm">
              <Phone className="w-4 h-4" /> Answer
            </button>
            <button onClick={() => actions.decline()}
              className="flex-1 px-3 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 inline-flex items-center justify-center gap-2 text-sm">
              <PhoneOff className="w-4 h-4" /> Decline
            </button>
          </div>
        ) : busy ? (
          <div className="space-y-2">
            {/* Consultation in progress — the caller is parked and the agent is
                speaking to the target. Completing connects the two directly. */}
            {state.consultPhase !== 'idle' ? (
              <div className="rounded-lg p-2.5 bg-amber-50 dark:bg-amber-900/30 space-y-2">
                <div className="text-xs text-amber-900 dark:text-amber-200">
                  {state.consultPhase === 'calling' ? 'ringing ' : 'talking to '}
                  <span className="font-mono">{state.consultTarget}</span> · caller on hold
                </div>
                <div className="flex gap-2">
                  <button onClick={() => actions.attendedComplete()}
                    disabled={state.consultPhase !== 'in-call'}
                    className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40 inline-flex items-center justify-center gap-1.5 text-sm">
                    <Check className="w-4 h-4" /> Complete
                  </button>
                  <button onClick={() => actions.attendedCancel()}
                    className="flex-1 px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 inline-flex items-center justify-center gap-1.5 text-sm">
                    <X className="w-4 h-4" /> Cancel
                  </button>
                </div>
              </div>
            ) : showXfer && (
              <div className="space-y-2">
                <input
                  value={xfer} onChange={e => setXfer(e.target.value)} autoFocus
                  placeholder="transfer to…"
                  className="w-full px-2.5 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-mono text-gray-800 dark:text-gray-100" />
                <div className="flex gap-2">
                  {/* Blind hands over immediately; attended lets the agent speak
                      to the target first and then connect the two. */}
                  <button onClick={() => { if (xfer) { actions.transfer(xfer); setShowXfer(false); setXfer(''); } }}
                    className="flex-1 px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm">Blind</button>
                  <button onClick={() => { if (xfer) { actions.attendedStart(xfer); setShowXfer(false); setXfer(''); } }}
                    className="flex-1 px-3 py-2 bg-amber-700 text-white rounded-lg hover:bg-amber-800 text-sm">Attended</button>
                </div>
              </div>
            )}

            {state.consultPhase === 'idle' && (
              <>
                <div className="flex gap-2">
                  <button onClick={() => setShowXfer(v => !v)}
                    className="flex-1 px-2 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 inline-flex items-center justify-center gap-1.5 text-xs">
                    <Forward className="w-3.5 h-3.5" /> Transfer
                  </button>
                  <button onClick={() => (state.onHold ? actions.unhold() : actions.hold())}
                    className="flex-1 px-2 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 inline-flex items-center justify-center gap-1.5 text-xs">
                    {state.onHold ? <><Play className="w-3.5 h-3.5" /> Resume</> : <><Pause className="w-3.5 h-3.5" /> Hold</>}
                  </button>
                  {/* Conference: send the far party to a room, then join it.
                      Both end up in the same bridge. */}
                  <button onClick={() => actions.conference('3000')} title="Move the caller into conference 3000 and join them"
                    className="flex-1 px-2 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 inline-flex items-center justify-center gap-1.5 text-xs">
                    <Users className="w-3.5 h-3.5" /> Conf
                  </button>
                </div>
                <button onClick={hangup}
                  className="w-full px-3 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 inline-flex items-center justify-center gap-2 text-sm">
                  <PhoneOff className="w-4 h-4" /> Hang up
                </button>
              </>
            )}
          </div>
        ) : online ? (
          <>
            <div className="flex gap-2">
              <input
                value={dest} onChange={e => setDest(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') actions.call(dest); }}
                placeholder="number"
                className="flex-1 px-2.5 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-mono text-gray-800 dark:text-gray-100" />
              <button onClick={() => actions.call(dest)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 inline-flex items-center gap-1.5 text-sm">
                <Phone className="w-4 h-4" /> Call
              </button>
            </div>
            <button onClick={() => actions.disconnect()}
              className="w-full px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 inline-flex items-center justify-center gap-1.5">
              <WifiOff className="w-3.5 h-3.5" /> Disconnect
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => (state.source === 'server' ? actions.provisionAndConnect() : actions.connect())}
              disabled={phase === 'connecting'}
              className="w-full px-3 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 inline-flex items-center justify-center gap-2 text-sm">
              <Wifi className="w-4 h-4" /> Connect &amp; Register
            </button>
            <p className="text-xs text-gray-500 text-center">
              {state.cfg.user}@{state.cfg.domain} · <Link to="/telephony/softphone" className="underline">settings</Link>
            </p>
          </>
        )}
      </div>

      {/* Wrap-up. OPTIONAL by design: Skip is a first-class button, not a
          dismiss-X, and nothing anywhere waits on this. A code an agent cannot
          avoid is a code chosen to make the box disappear. */}
      {wrapUp && (
        <div className="border-t border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
          <p className="mb-2 text-xs font-medium text-gray-700 dark:text-gray-200">
            How did that call end?
            {wrapUp.number && <span className="ml-1 font-mono opacity-70">{wrapUp.number}</span>}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {codes.map(d => (
              <button
                key={d.code}
                onClick={async () => {
                  // Closed first: the code is already captured, and leaving the
                  // panel up while the request flies invites a second click and
                  // a duplicate row.
                  const n = wrapUp.number;
                  setWrapUp(null);
                  try { await agentConsoleService.fileDisposition(d.code, n || undefined); } catch { /* optional — never block the agent */ }
                }}
                className="rounded border border-gray-300 px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                {d.label}
              </button>
            ))}
            <button onClick={() => setWrapUp(null)}
              className="rounded px-2 py-1 text-[11px] text-gray-500 hover:underline">
              Skip
            </button>
          </div>
        </div>
      )}

      {/* Capture the caller on the line. Reuses the contacts modal rather than a
          cut-down form, so a contact saved mid-call is a normal contact — same
          validation, same fields — not a second-class record to clean up later. */}
      <ContactEditModal
        isOpen={saveOpen}
        prefillPhone={saveNumber}
        onClose={() => setSaveOpen(false)}
        onSuccess={() => {
          setSaveOpen(false);
          // Re-run the lookup so the panel switches from "not in contacts" to
          // the name just saved, while the call is very likely still up.
          actions.refreshCallerProfile();
        }}
      />
    </div>
  );
};

export default SoftphoneWidget;
